import { ChangeDetectorRef, Component } from '@angular/core';
import { CaseProduct } from '../../../../../form-application/mortgage-application/models/case-product.model';
import { ReferenceDataCategoriesConstants } from '../../../../../lib/constants/reference-data-categories';
import { BaseMortgageApplicationComponent } from '../../../pages/base-mortgage-application.component';
import { Address } from './../../../../../lib/models/api/address.model';
import { Associate } from './../../../../../lib/models/api/associate.model';
import { ReferenceDataService } from './../../../../../lib/services/reference-data.service';
import { MortgageApplicationStore } from './../../../services/mortgage-application-store';
import { MortgageApplicationService } from './../../../services/mortgage-application.service';

@Component({
  selector: 'boi-your-property-legal-support',
  templateUrl: './your-property-legal-support.component.html',
  styleUrls: ['./your-property-legal-support.component.scss']
})
export class YourPropertyLegalSupportComponent extends BaseMortgageApplicationComponent {
  public isLmsStatementVisible = true;
  public isWithoutSolicitorVisible = true;
  public isFeesFreeStatementVisible = true;
  public isSolicitorListVisible: boolean;
  public solicitorSearchPostCode: string;
  public selectedSolicitor: Associate;
  public solicitors: Array<Associate>;
  public showContactLMS = false;
  public contactLMSAnswer: string;
  private readonly LMSReferral = 'LMS Referral';

  constructor(
    protected mortgageApplicationStore: MortgageApplicationStore,
    private readonly referenceDataService: ReferenceDataService,
    private readonly mortgageApplicationService: MortgageApplicationService,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(mortgageApplicationStore);
  }

  isSolicitorAddressFieldVisible(): boolean {
    return (
      (this.mortgageApplication.SolicitorChosenCode === this.referenceDataOptionLists.AnswerTypeYes &&
        !this.mortgageApplication.QuoteFromLegalPartnersCode &&
        !this.mortgageApplication.SolicitorFromPanelCode) ||
      (this.contactLMSAnswer === this.referenceDataOptionLists.AnswerTypeYes &&
        !this.mortgageApplication?.IsApplicationReadOnly)
    );
  }

  searchSolicitor(): void {
    this.selectedSolicitor = undefined;
    this.solicitors = [];
    if (this.solicitorSearchPostCode) {
      this.isSolicitorListVisible = true;
      this.mortgageApplicationService.getSolicitors(this.solicitorSearchPostCode).subscribe((data) => {
        this.solicitors = data;
        this.cdr.detectChanges();
      });
    }
  }

  selectSolicitor(): void {
    this.mortgageApplication.SolicitorFromPanelCode = this.referenceDataOptionLists.AnswerTypeYes;
    this.populateSolicitor(this.selectedSolicitor);
    this.showContactLMS = false;
    this.contactLMSAnswer = undefined;
  }

  populateSolicitor(associate: Associate): void {
    if (associate) {
      const id =
        this.mortgageApplication.SolicitorAddress !== null
          ? this.mortgageApplication.SolicitorAddress.AddressID
          : undefined;
      this.mortgageApplication.SolicitorSequenceNumber = associate.AssociateSequenceNumber;

      this.mortgageApplication.SolicitorAddress.Name = associate.Name;
      this.mortgageApplication.SolicitorAddress.FromPaf = false;
      this.mortgageApplication.SolicitorAddress.AddressLine1 = associate.AddressLine1;
      this.mortgageApplication.SolicitorAddress.AddressLine2 = associate.AddressLine2;
      this.mortgageApplication.SolicitorAddress.AddressLine3 = associate.AddressLine3;
      this.mortgageApplication.SolicitorAddress.PhoneNumber = associate.PhoneNumber;
      this.mortgageApplication.SolicitorAddress.County = associate.County;
      this.mortgageApplication.SolicitorAddress.Postcode = associate.PostCode;
      this.mortgageApplication.SolicitorAddress.AddressID = id;
    }
  }

  resetSolicitorDetails(): void {
    this.solicitorSearchPostCode = undefined;
    this.isSolicitorListVisible = false;
    this.mortgageApplication.SolicitorSequenceNumber = undefined;
    this.mortgageApplication.SolicitorFromPanelCode = undefined;
    this.mortgageApplication.QuoteFromLegalPartnersCode = undefined;
    this.mortgageApplication.IndividualActing = undefined;
    this.mortgageApplication.SolicitorAddress = new Address();
    this.showContactLMS = false;
    this.contactLMSAnswer = undefined;
  }

  isSolicitorLmsReferral(): boolean {
    if (
      (this.mortgageApplication.QuoteFromLegalPartnersCode === this.referenceDataOptionLists.AnswerTypeYes &&
        this.mortgageApplication.IsApplicationReadOnly) ||
      this.mortgageApplication.hasLegalServiceFree()
    ) {
      this.mortgageApplication.SolicitorAddress.Name = this.LMSReferral;
      this.mortgageApplication.SolicitorAddress.FromPaf = false;

      return true;
    }
    if (!this.mortgageApplication.IsApplicationReadOnly) {
      this.mortgageApplication.QuoteFromLegalPartnersCode = null;
    }

    return false;
  }

  isSolicitorFirmNameVisible(): boolean {
    return this.isSolicitorChosenOrQuote() || this.mortgageApplication.hasLegalServiceFree();
  }

  isSolicitorChosenFromPanel(): boolean {
    return (
      this.mortgageApplication.SolicitorFromPanelCode === this.referenceDataOptionLists.AnswerTypeYes &&
      this.isSolicitorFromPanel()
    );
  }

  isSolicitorFromPanel(): boolean {
    const hasSolicitorSequenceNo =
      this.mortgageApplication.SolicitorSequenceNumber !== undefined &&
      this.mortgageApplication.SolicitorSequenceNumber !== null;
    if (hasSolicitorSequenceNo) {
      return this.mortgageApplication.SolicitorSequenceNumber.toString() !== '';
    }
  }

  wasSolicitorChosenManually(): boolean {
    return (
      this.mortgageApplication.SolicitorFromPanelCode === this.referenceDataOptionLists.AnswerTypeNo &&
      this.mortgageApplication.SolicitorAddress.Postcode &&
      this.mortgageApplication.IsApplicationReadOnly
    );
  }

  isSolicitorChosenOrQuote(): boolean {
    return !(
      this.mortgageApplication.SolicitorChosenCode === this.referenceDataOptionLists.AnswerTypeNo ||
      this.mortgageApplication.SolicitorChosenCode === null ||
      this.mortgageApplication.SolicitorFromPanelCode === null
    );
  }

  isStatementVisible(statement: boolean): boolean {
    return statement && this.mortgageApplication.SolicitorChosenCode === this.referenceDataOptionLists.AnswerTypeNo;
  }

  getFeesFreeStatement(): string {
    let feesFreeStatement = '';
    let caseProduct: CaseProduct;

    if (this.mortgageApplication.ApplicationProducts[0]) {
      caseProduct = this.mortgageApplication.ApplicationProducts[0].CaseProduct;
    }

    if (caseProduct !== undefined) {
      if (caseProduct.Product !== undefined) {
        if (this.mortgageApplication.isRemortgageWithFreeLegalService(caseProduct)) {
          const remortgageTextCode = caseProduct.Product.FreeLegalServiceForRemortgageTextCode
            ? caseProduct.Product.FreeLegalServiceForRemortgageTextCode
            : undefined;
          feesFreeStatement = this.referenceDataService.getConfigurationValueByCode(
            ReferenceDataCategoriesConstants.LABEL,
            this.referenceDataLabels.FeesFreeTextRemortgage,
            remortgageTextCode
          );
        } else if (this.mortgageApplication.isPurchaseWithFreeLegalService(caseProduct)) {
          const purchaseTextCode = caseProduct.Product.FreeLegalServiceForPurchaseTextCode
            ? caseProduct.Product.FreeLegalServiceForPurchaseTextCode
            : undefined;
          feesFreeStatement = this.referenceDataService.getConfigurationValueByCode(
            ReferenceDataCategoriesConstants.LABEL,
            this.referenceDataLabels.FeesFreeTextPurchase,
            purchaseTextCode
          );
        }
      }
    }

    return feesFreeStatement;
  }

  public cantFindSolicitor(): void {
    this.showContactLMS = true;
  }

  public showLMSAnswer(): void {
    this.mortgageApplication.SolicitorFromPanelCode =
      this.contactLMSAnswer === this.referenceDataOptionLists.AnswerTypeYes
        ? this.referenceDataOptionLists.AnswerTypeNo
        : null;

    if (this.mortgageApplication.SolicitorFromPanelCode === this.referenceDataOptionLists.AnswerTypeNo)
      this.mortgageApplication.SolicitorAddress.Name = this.referenceDataService.getLocalizeLabel(
        this.referenceDataLabels.AddSolicitorToPanel
      );
  }

  displayAddressFormForSolicitorChosenManually(): boolean {
    return (
      this.wasSolicitorChosenManually() &&
      !this.mortgageApplication.QuoteFromLegalPartnersCode &&
      this.mortgageApplication.SolicitorChosenCode === this.referenceDataOptionLists.AnswerTypeYes
    );
  }
}
